import { useMutation, gql } from "@apollo/client";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { GET_MEMBERSHIP_DETAILS_QUERY } from "../../person/queries";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

const DELETE_MEMBER_BAND_MUTATION = gql`
  mutation DeleteMemberBand($id: ID!) {
    deleteMemberBand(id: $id) {
      success
      message
    }
  }
`;

interface DeleteMembershipModalProps {
  open: boolean;
  onClose: () => void;
  bandId: string;
  pgaId: string;
}

export default function DeleteMembershipModal({ open, onClose, bandId, pgaId }: DeleteMembershipModalProps) {
  const [deleteMemberBand, { loading }] = useMutation(DELETE_MEMBER_BAND_MUTATION, {
    refetchQueries: [
      {
        query: GET_MEMBERSHIP_DETAILS_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  async function handleDelete() {
    try {
      const { data } = await deleteMemberBand({ variables: { id: bandId } });

      if (data?.deleteMemberBand?.success) {
        showSnackbar(data.deleteMemberBand.message || "Membership record deleted");
        onClose();
      } else {
        showErrorSnackbar(data?.deleteMemberBand?.message || "Failed to delete membership record");
      }
    } catch (e) {
      const errorMessage = (e as Error).message || "An error occurred while deleting the membership record";
      showErrorSnackbar(`Error: ${errorMessage}`);
    }
  }

  return (
    <Dialog
      className="p-6 w-[400px]"
      open={open}
      onClosed={onClose}
      headline="Delete Membership Record"
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="delete-membership-cancel-button">
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleDelete}
            testId="delete-membership-confirm-button"
            disabled={loading}
            theme="error"
            icon="delete"
          >
            Delete
          </Button>
        </>
      }
    >
      <p className="text-body-large text-sys-brand-on-surface">
        Are you sure you want to delete this membership record? This cannot be undone.
      </p>
    </Dialog>
  );
}
